import apiClient from './api';
import { ProjectReport, Finding } from '../types/api.types';
import { findingService } from './findingService';


/**
 * Request interface for report generation
 */
export interface ReportGenerationRequest {
  project_id: string;
  title: string;
  include_artifacts: boolean;
  include_findings: boolean;
  query?: string;
  max_findings?: number;
  max_artifacts?: number;
}

/**
 * Service for managing project reports
 */
export class ReportService {
  private reportsBaseUrl = '/pentest/ui/reports';
  
  /**
   * Get URL to directly access a report's content
   */
  getReportFileUrl(reportId: string): string {
    return `${apiClient.getBaseUrl()}${this.reportsBaseUrl}/${reportId}/content`;
  }

  /**
   * Generate a new report for a project using the dedicated report API
   * 
   * @param projectId Project ID to generate report for
   * @param title Title for the report (or includeArtifacts if boolean)
   * @param includeArtifacts Whether to include artifacts in the report (or includeFindings if boolean)
   * @param includeFindings Whether to include findings in the report (or sessionId if string)
   * @param query Optional search query to filter findings by relevance
   * @returns A promise with the generation status
   */
  async generateReport(
    projectId: string,
    title: string = "Penetration Test Report",
    includeArtifacts: boolean = true,
    includeFindings: boolean = true,
    query?: string
  ): Promise<{ report_id: string; status: string; message: string }> {
    try {
      
      const requestData: ReportGenerationRequest = {
        project_id: projectId,
        title,
        include_artifacts: includeArtifacts,
        include_findings: includeFindings
      };

      // Add optional query if provided
      if (query) {
        requestData.query = query;
      }

      // Call the report generation endpoint
      const response = await apiClient.post<{ report_id: string; status: string; message: string }>(
        `${this.reportsBaseUrl}/generate`,
        requestData
      );

      return response;
    } catch (error) {
      console.error('Error generating report:', error);
      throw error;
    }
  }

  /**
   * Get all reports for a project
   * 
   * @param projectId Project ID to get reports for
   * @returns A promise with an array of reports
   */
  async getReports(projectId: string): Promise<ProjectReport[]> {
    try {
      return apiClient.get<ProjectReport[]>(`${this.reportsBaseUrl}?project_id=${projectId}`);
    } catch (error) {
      console.error('Error getting reports:', error);
      throw error;
    }
  }

  /**
   * Get a specific report by ID
   * 
   * @param reportId Report ID to get
   * @returns A promise with the report data
   */
  async getReport(reportId: string): Promise<ProjectReport> {
    try {
      return apiClient.get<ProjectReport>(`${this.reportsBaseUrl}/${reportId}`);
    } catch (error) {
      console.error('Error getting report:', error);
      throw error;
    }
  }

  /**
   * Get the latest report for a project
   * 
   * @param projectId Project ID to get report for
   * @returns A promise with the report data or null if no reports exist
   */
  async getLatestReport(projectId: string): Promise<ProjectReport | null> {
    try {
      const reports = await this.getReports(projectId);
      
      // Sort by created_at descending and get the first one
      const sortedReports = [...reports].sort((a, b) => {
        const dateA = new Date(a.created_at).getTime();
        const dateB = new Date(b.created_at).getTime();
        return dateB - dateA;
      });
      
      return sortedReports.length > 0 ? sortedReports[0] : null;
    } catch (error) {
      console.error('Error getting latest report:', error);
      return null;
    }
  }

  /**
   * Check if a project has a completed report
   * 
   * @param projectId Project ID to check
   * @returns A promise that resolves to true if the project has at least one completed report
   */
  async hasCompletedReport(projectId: string): Promise<boolean> {
    try {
      const reports = await this.getReports(projectId);
      return reports.some(report => report.status === 'completed');
    } catch (error) {
      console.error('Error checking for reports:', error);
      return false;
    }
  }
  
  /**
   * Delete a report by ID
   * 
   * @param reportId Report ID to delete
   * @returns A promise that resolves to a success message
   */
  async deleteReport(reportId: string): Promise<{ message: string }> {
    try {
      return apiClient.delete<{ message: string }>(`${this.reportsBaseUrl}/${reportId}`);
    } catch (error) {
      console.error('Error deleting report:', error);
      throw error;
    }
  }

  /**
   * Get the status of a report generation task
   * 
   * @param reportId Report ID to check status for
   * @returns The current status of the report generation
   */
  async getReportStatus(reportId: string): Promise<string> {
    try {
      const report = await this.getReport(reportId);
      return report.status;
    } catch (error) {
      console.error('Error getting report status:', error);
      return 'error';
    }
  }
  
  /**
   * Get all report findings for a project
   * 
   * @param projectId Project ID to get reports for
   * @returns An array of report findings or empty array if none exist
   */
  async getReportFindings(projectId: string): Promise<Finding[]> {
    try {
      // Look for findings with type='report' or name containing "report"
      const findings = await apiClient.get<Finding[]>(`/pentest/ui/findings?project_id=${projectId}`);
      if (findings && Array.isArray(findings)) {
        // Sort by created_at (newest first)
        const sortedFindings = [...findings].sort((a, b) => {
          const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
          const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
          return dateB - dateA; // Descending order (newest first)
        });
        
        // Filter findings that are reports
        const reportFindings = sortedFindings.filter(finding => 
          (finding.type === 'report' || (finding.name && finding.name.toLowerCase().includes('report')))
        );
        
        return reportFindings;
      }
      return [];
    } catch (error) {
      console.error('Error getting report findings:', error);
      return [];
    }
  }
  
  /**
   * Get the latest report finding for a project
   * 
   * @param projectId Project ID to get report for
   * @returns The latest report finding or null if none exists
   */
  async getLatestReportFinding(projectId: string): Promise<Finding | null> {
    const reports = await this.getReportFindings(projectId);
    return reports.length > 0 ? reports[0] : null;
  }
  
  /**
   * Get the content of a specific report by ID
   * 
   * @param reportId Report finding ID to get content for
   * @returns The report content as a string, or null if no report exists
   */
  async getReportContentById(reportId: string): Promise<string | null> {
    try {
      const report = await this.getReport(reportId);
      if (report && report.content) {
        return report.content;
      }
      return null;
    } catch (error) {
      console.error('Error getting report content:', error);
      return null;
    }
  }
  
  /**
   * Get the content of the latest report
   * 
   * @param projectId Project ID to get report content for
   * @returns The report content as a string, or null if no report exists
   */
  async getReportContent(projectId: string): Promise<string | null> {
    try {
      // First try new-style reports
      const latestReport = await this.getLatestReport(projectId);
      if (latestReport && latestReport.content) {
        return latestReport.content;
      }
      
      // Fall back to legacy finding-based reports
      const reportFinding = await this.getLatestReportFinding(projectId);
      
      if (!reportFinding || !reportFinding.has_file) {
        return null;
      }
      
      // Get the file content
      return this.getReportContentById(reportFinding.id);
    } catch (error) {
      console.error('Error getting report content:', error);
      return null;
    }
  }

  /**
   * Download a report in the specified format (markdown or word)
   *
   * @param reportId Report ID to download
   * @param format Format to download ('markdown' or 'word')
   * @returns A promise that resolves when the download starts
   */
  async downloadReport(reportId: string, format: 'markdown' | 'word' = 'markdown'): Promise<void> {
    try {
      console.log(`ReportService: Starting authenticated download for report ${reportId} in ${format} format`);
      
      // Use authenticated API request instead of direct link to avoid auth issues
      const downloadUrl = `${this.reportsBaseUrl}/${reportId}/download?format=${format}`;
      console.log(`ReportService: Download URL: ${downloadUrl}`);
      
      // Fetch the file using authenticated API client
      const blob = await apiClient.getBlob(downloadUrl);
      console.log(`ReportService: Successfully received blob, size: ${blob.size} bytes`);
      
      // Create blob URL and trigger download
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `report-${reportId}.${format === 'word' ? 'docx' : 'md'}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the blob URL
      URL.revokeObjectURL(url);
      console.log(`ReportService: Download completed successfully`);
    } catch (error) {
      console.error(`Error downloading report in ${format} format:`, error);
      throw error;
    }
  }  /**
   * Get an authenticated download URL for a report (for direct browser navigation)
   * This method is deprecated - use downloadReport() instead for proper authenticated downloads
   * 
   * @param reportId Report ID to download
   * @param format Format to download ('markdown' or 'word')
   * @returns Always returns null - use downloadReport() method instead
   */
  async getAuthenticatedDownloadUrl(reportId: string, format: 'markdown' | 'word' = 'markdown'): Promise<string | null> {
    console.warn('getAuthenticatedDownloadUrl is deprecated. Use downloadReport() method instead for proper authenticated downloads.');
    
    // Instead of returning a URL that requires manual authentication,
    // trigger the proper authenticated download directly
    try {
      await this.downloadReport(reportId, format);
      return null; // Download was triggered, no URL needed
    } catch (error) {
      console.error('Error triggering authenticated download:', error);
      return null;
    }
  }

  /**
   * Download a report as a Word document (.docx)
   *
   * @param reportId Report ID to download
   * @returns A promise that resolves when the download starts
   */
  async downloadReportAsWord(reportId: string): Promise<void> {
    return this.downloadReport(reportId, 'word');
  }

  /**
   * Download a report as a Markdown document (.md)
   * 
   * @param reportId Report ID to download
   * @returns A promise that resolves when the download starts
   */
  async downloadReportAsMarkdown(reportId: string): Promise<void> {
    return this.downloadReport(reportId, 'markdown');
  }
}

// Export singleton instance
export const reportService = new ReportService();
export default reportService;